import { Fragment } from "react";
import { inputChipClass, selectChipClass } from "@/lib/forms";
import type { ModelTemplate, ModelTemplateField } from "@/modules/plus/plus.schema";

// Build a code field by field, *inline*. Each enum field becomes a styled
// <select> that sits in the code string itself; free-text fields become an
// auto-sizing <input>. Below the inline string, a tight summary lists each
// chosen segment with its meaning — no photos at this layer; the gallery
// section below the picker handles all photo display.
//
// State is owned by the parent (ModelDetail) so other surfaces (gallery
// upload, gallery filtering) can read the current selection.

const enumEntries = (field: ModelTemplateField): Array<[string, string]> =>
  field.codes ? Object.entries(field.codes) : [];

export const defaultForField = (
  field: ModelTemplateField,
  isModelField: boolean,
  seriesId: string,
): string => {
  if (isModelField) {
    const codes = Object.keys(field.codes ?? {});
    return codes.includes(seriesId) ? seriesId : (codes[0] ?? seriesId);
  }
  const entries = enumEntries(field);
  if (entries.length === 0) {
    return "";
  }
  const def = entries.find(([, meaning]) => /default/i.test(meaning));
  return def?.[0] ?? entries[0]?.[0] ?? "";
};

// Compose a dash-joined ordering key from the values record. Empty segments
// are skipped so a partial selection still produces a clean string.
export const composeCode = (template: ModelTemplate, values: Record<number, string>): string =>
  template.fields
    .map((_f, i) => values[i] ?? "")
    .filter((v) => v !== "")
    .join("-");

export interface ValuePickerProps {
  template: ModelTemplate;
  values: Record<number, string>;
  onChange: (values: Record<number, string>) => void;
  onReset: () => void;
}

export const ValuePicker: React.FC<ValuePickerProps> = ({
  template,
  values,
  onChange,
  onReset,
}) => {
  const segments = template.fields.map((_f, i) => values[i] ?? "");

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Build a code
        </h2>
        <button
          type="button"
          onClick={onReset}
          className="text-xs text-blue-600 hover:underline dark:text-blue-400"
        >
          Reset
        </button>
      </div>

      <div className="mt-3 rounded-lg bg-gray-50 px-4 py-3 font-mono text-base dark:bg-gray-950">
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-2">
          {template.fields.map((f, i) => {
            const opts = enumEntries(f);
            const isEnum = opts.length > 0;
            const current = values[i] ?? "";
            const currentMeaning =
              f.codes && Object.hasOwn(f.codes, current) ? f.codes[current] : null;

            // Native <select> displays its selected <option>'s text, so each
            // option's text *is* the inline display. We keep the meaning in
            // the dropdown menu (where users actually pick) but make the
            // collapsed display just the code, matching the catalog format.
            const node = isEnum ? (
              <select
                aria-label={f.name}
                value={current}
                onChange={(e) => onChange({ ...values, [i]: e.target.value })}
                style={{ width: `${Math.max(current.length, 1) + 1.5}ch` }}
                className={selectChipClass}
                title={currentMeaning ? `${f.name}: ${currentMeaning}` : f.name}
              >
                {opts.map(([code, meaning]) => (
                  <option key={code} value={code} title={meaning}>
                    {code === current ? code : `${code} — ${meaning}`}
                  </option>
                ))}
              </select>
            ) : (
              <input
                aria-label={f.name}
                type="text"
                value={current}
                onChange={(e) => onChange({ ...values, [i]: e.target.value })}
                placeholder={f.values ?? "value"}
                style={{ width: `${Math.max(current.length, 4) + 1}ch` }}
                className={inputChipClass}
                title={f.name}
              />
            );

            return (
              // biome-ignore lint/suspicious/noArrayIndexKey: template fields are positional by definition
              <Fragment key={i}>
                {i > 0 && <span className="text-gray-400 dark:text-gray-600">-</span>}
                {node}
              </Fragment>
            );
          })}
        </div>
      </div>

      <dl className="mt-4 grid grid-cols-[max-content_max-content_1fr] gap-x-3 gap-y-1 text-xs">
        {template.fields.map((f, i) => {
          const current = segments[i];
          if (!current) {
            return null;
          }
          const meaning = f.codes && Object.hasOwn(f.codes, current) ? f.codes[current] : null;
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: template fields are positional by definition
            <Fragment key={i}>
              <dt className="text-gray-500 dark:text-gray-400">
                {f.position}. {f.name}
              </dt>
              <dd className="font-mono font-semibold text-gray-900 dark:text-gray-100">
                {current}
              </dd>
              <dd className="truncate text-gray-600 dark:text-gray-400">{meaning ?? ""}</dd>
            </Fragment>
          );
        })}
      </dl>
    </section>
  );
};
